import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getEventById } from "ticketino-api-client";
import { Calendar } from "react-calendar";
import { toZonedTime } from 'date-fns-tz';

import Loading from "./components/Loading";
import Header from "./components/Header";

import "../css/style.css";
import "../css/calendar.css";

const Home = () => {
    const [events, setEvents] = useState([]);
    const [eventGroup, setEventGroup] = useState({});
    const [orderId, setOrderId] = useState();
    const [eventDates, setEventDates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedEventId, setSelectedEventId] = useState(0);

    const [eventGroupId, setEventGroupId] = useState(0);

    const timeZone = 'Europe/Zurich';
    const today = new Date();
    const zurichDate = toZonedTime(today, timeZone);  // Convert to Zurich time
    // Create the reference date (Jan 1, 2026) in Zurich time
    const referenceDate = toZonedTime(new Date(Date.UTC(2026, 0, 1)), timeZone);
    const isAfterReference = zurichDate > referenceDate;
    const defaultMonth = isAfterReference ? zurichDate.getMonth() : referenceDate.getMonth();
    const defaultYear = isAfterReference ? zurichDate.getFullYear() : referenceDate.getFullYear();
    const [currentMonth, setCurrentMonth] = useState(new Date(defaultYear, defaultMonth, 1));

    const [date, setDate] = useState(isAfterReference ? zurichDate : referenceDate);

    const { language } = useParams();

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const [resources, setResources] = useState({});

    useEffect(() => {
        const loadToken = async () => {
            try {
                const res = await axios.get("form/token");
                axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
                api.defaults.headers.common['Authorization'] = "Bearer " + res.data;
                sessionStorage.setItem("token", res.data);
                requestFormSettings();
            } catch (error) {
                console.error(error);
            }
        };

        const requestFormSettings = async () => {
            try {
                const res = await axios.get(`form/formsettings`);
                startOrder(res.data.posId);
                setEventGroupIds(res.data);
            } catch (error) {
                console.error(error);
            }
        };

        const setEventGroupIds = (data) => {
            setEventGroupId(data.eventGroupId);
        };

        const requestResources = async () => {
            try {
                const res = await axios.get(`form/resources/${language}`);
                setResources(res.data?.translation);
            } catch (error) {
                console.error(error);
            }
        };

        loadToken();
        requestResources();
    }, [language]);

    useEffect(() => {
        if (orderId) {
            loadEvents(eventGroupId, currentMonth);
        }
    }, [currentMonth]);

    const startOrder = async (posId) => {
        const order = {
            affiliateShopId: null,
            currency: "CHF",
            tenantId: 1,
            pointOfSaleId: posId,
            abbreviation: "",
            paymentType: 1,
        };

        try {
            const res = await axios.post(`${baseUrl}/ShopBasket/Order`, order);
            sessionStorage.setItem("OrderId", res.data.id);
            setOrderId(res.data.id);
            setCurrentMonth(new Date(defaultYear, defaultMonth, 1));
        } catch (error) {
            console.error(error);
        }
    };

    const loadEvents = async (eventGroupId, currentMonth) => {
        setLoading(true);

        try {
            // EventGroup
            const eventGroup = await axios.get(`${baseUrl}/EventGroup/${eventGroupId}`);

            const totalNumberOfEventsResponse = await axios.get(`${baseUrl}/EventGroup/${eventGroupId}/Events/Count`);
            const totalNumberOfEvents = totalNumberOfEventsResponse.data;
            const eventBatchSize = 100;
            let events = [];

            for (let i = 0; i <= totalNumberOfEvents; i += eventBatchSize) {
                const eventGroupEventsResponse = await axios.get(`${baseUrl}/EventGroup/Events?eventGroupId=${eventGroupId}&languageCode=${language}&numberToTake=${eventBatchSize}&numberToSkip=${i}`);
                events = events.concat(eventGroupEventsResponse.data?.events);
            }

            // Check if the event date is within the current month and in the future
            const eventsForCurrentMonth = events.filter((event) => {
                const eventDate = new Date(event.start);
                const startOfMonth = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
                const endOfMonth = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
                return eventDate >= startOfMonth && eventDate < endOfMonth && eventDate >= today;
            });

            const datesArray = [];

            await Promise.all(eventsForCurrentMonth.map(async (event) => {
                const ticketCountResponse = await requestTicketCount(event.id);

                const theEvent = await getEventById(event.id);

                let soldOut = false;

                soldOut = (theEvent?.maxTickets - ticketCountResponse) <= 0;

                //if (!soldOut) {
                //    datesArray.push(event.start?.split('T')[0]);
                //}

                datesArray.push(event.start?.split('T')[0]);

                event.end = theEvent?.end;
                event.isSoldOut = soldOut;
                event.maxTickets = theEvent?.maxTickets;
                event.ticketCount = theEvent?.maxTickets - ticketCountResponse;

                event.eventGroupId = eventGroupId;

            })).then(() => {
                setEventDates(datesArray);
                setEvents(events);
                setEventGroup(eventGroup.data)
                setLoading(false);
            });
        } catch (error) {
            console.error(error);
        }
    };

    const requestTicketCount = async (id) => {
        try {
            const res = await axios.get(`${baseUrl}/Event/${id}/TicketsSold`);
            return res.data;
        } catch (error) {
            console.error(error);
        }
    };

    const tileClassName = ({ date }) => {
        // Create a UTC date from the input
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Format the date as ISO string in UTC
        const formattedDate = utcDate.toISOString().split('T')[0];
        return eventDates.includes(formattedDate) ? 'bold-date' : 'disabled-date';
    };

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setCurrentMonth(activeStartDate);

        // clear old values
        setSelectedDate("");
        setSelectedEventId(0);
    };

    const onClickDay = (date) => {
        let selectedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const formattedDate = selectedDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);

        // clear old values
        setSelectedEventId(0);
    };

    const onCheckedChange = (e) => setSelectedEventId(e.target.value);

    const mapEventStartTimes = () => {
        const currentEventsByDate = events.filter(e => e.start?.split('T')[0] === selectedDate);

        return (
            <div style={{ marginTop: '1rem' }}>
                {currentEventsByDate?.length > 0 && currentEventsByDate.map((currentEvent) => {
                    const eventDateStart = new Date(currentEvent.start);
                    const eventTimeStart = formatTime(eventDateStart);

                    const eventDateEnd = new Date(currentEvent.end);
                    const eventTimeEnd = formatTime(eventDateEnd);

                    let description = eventTimeStart + " - " + eventTimeEnd + " Uhr";

                    if (currentEvent.isSoldOut) {
                        description = eventTimeStart + " - " + eventTimeEnd + " Uhr - AUSVERKAUFT";
                    }

                    return (
                        <div key={currentEvent.id} style={{ maxWidth: '210px', textAlign: 'left', margin: '0 auto 1rem auto', display: 'flex', alignItems: 'center' }}>
                            <input
                                className="mt-2"
                                type="radio"
                                id={"start-" + currentEvent.id}
                                name="event-start"
                                value={currentEvent.id}
                                onChange={(e) => onCheckedChange(e)}
                                disabled={currentEvent.isSoldOut}
                                required
                            />
                            <label className="ms-3 mt-2 f-size-20px label-radio" htmlFor={"start-" + currentEvent.id} style={{ wordBreak: 'break-word', flex: 1 }}>
                                {description}
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    };

    const formatTime = (date) => {
        // Convert the date to Europe/Zurich time and format it to 24-hour format
        const options = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Zurich' // Ensure the time is in Swiss time zone
        };
        return new Intl.DateTimeFormat('de-CH', options).format(date);
    };

    // check if a date is weekend
    const isWeekend = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDay(); // 0 = Sunday, 6 = Saturday
        return day === 0 || day === 6;
    };

    const handleNavigateSingleBooking = () => {
        navigate(`/${language}/tickets/${eventGroupId}/${selectedEventId}`)
    };

    return (
        <div>
            {/*{loading && <Loading alignment="center" color="#d3d3d3" bgColor="transparent" position="fixed" top="300px" />}*/}
            <>
                <Header />

                {resources && (
                    <div className="container mb-3">

                        <div className="row">

                            <div className="col-md-5 text-center" style={{ color: '#FFFFFF' }}>
                                <p dangerouslySetInnerHTML={{ __html: resources?.FlowEventsRule }}></p>
                            </div>

                            <div className="col-md-7">
                                <div className="row">
                                    <div className="col-md-12 f-size-20px text-center">
                                        <p>{resources?.TicketCalendarChooseTime}</p>
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <div className={loading ? "isHidden" : ""}>
                                            <Calendar
                                                view="month"
                                                value={date}
                                                onChange={setDate}
                                                tileClassName={tileClassName}
                                                onClickDay={onClickDay}
                                                locale={language}
                                                minDate={new Date(defaultYear, defaultMonth, 1)}
                                                onActiveStartDateChange={handleActiveStartDateChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-12">
                                        {!loading && selectedDate && (
                                            <div>
                                                <div className="row mt-5">
                                                    <div className="col-md-12 f-size-20px text-center">
                                                        <p>{resources?.TicketCalendarChooseTime}</p>
                                                    </div>
                                                </div>
                                                {mapEventStartTimes()}
                                            </div>
                                        )}
                                        <div className="row mt-5 mb-4">
                                            <div className="col-6">
                                                <button className="btn custom-button white" onClick={() => navigate(-1)}>
                                                    {resources?._Back}
                                                </button>
                                            </div>
                                            <div className="col-6 text-end">
                                                {/* Single booking */}
                                                <button
                                                    className="btn custom-button"
                                                    disabled={!(selectedEventId > 0)}
                                                    onClick={() => handleNavigateSingleBooking()}
                                                >
                                                    WEITER
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                )}
            </>
        </div>
    );
};

export default Home;